import React, { useState } from 'react';
import { Box, Image, Text, VStack, HStack, Icon, useBreakpointValue, IconButton } from '@chakra-ui/react';
import { MdAccessTime, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface PodcastRecommendationProps {
  episodeId: string;
  reason: string;
  podcastImage: string;
  episodeTitle: string;
  podcastName: string;
  daysAgo: number | null;
}

const PodcastRecommendationCard: React.FC<PodcastRecommendationProps> = ({
  episodeId,
  reason,
  podcastImage,
  episodeTitle,
  podcastName,
  daysAgo,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const imageSize = useBreakpointValue({ base: '120px', sm: '140px' });
  const padding = useBreakpointValue({ base: 3, md: 4 });

  const formatDaysAgo = (days: number | null): string => {
    if (days === null) return 'N/A';
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (e.target instanceof Element && e.target.closest('.expand-button')) {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    } else {
      navigate(`/episodes/${encodeURIComponent(episodeId)}`);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={{ base: 'row', sm: 'row' }}
      mb={4}
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        transform: 'scale(1.01)',
        boxShadow: 'lg',
        cursor: 'pointer'
      }}
      onClick={handleClick}
    >
      <Box flexShrink={0} paddingLeft={5} paddingTop={5}>
        <Box
          position="relative"
          width={imageSize}
          height={imageSize}
        >
          <Image
            src={podcastImage}
            alt={`${podcastName} cover`}
            width="100%"
            height="100%"
            objectFit="cover"
            borderRadius="md"
          />
        </Box>
        <HStack 
          spacing={1} 
          justify="center" 
          py={1} 
          px={2}
          bg="gray.50"
          borderRadius="md"
          mt={2}
        >
          <Icon as={MdAccessTime} color="gray.500" fontSize="xs" />
          <Text 
            color="gray.500" 
            fontSize="xs" 
            whiteSpace="nowrap"
          >
            {formatDaysAgo(daysAgo)}
          </Text>
        </HStack>
      </Box>
      <Box flex="1" p={padding}>
        <VStack align="start" spacing={3} height="100%">
          <Box width="100%">
            <Text 
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight="semibold"
              noOfLines={2}
              lineHeight="shorter"
              mb={1}
            >
              {episodeTitle}
            </Text>
            <Text
              fontSize="sm"
              color="gray.600"
              mb={2}
            >
              {podcastName}
            </Text>
          </Box>
          <Box
            width="100%"
            position="relative"
          >
            <Box>
              <Text
                color="gray.600"
                fontSize={{ base: 'sm', md: 'sm' }}
                noOfLines={isExpanded ? undefined : 3}
                mb={2}
              >
                {reason}
              </Text>
              <Box
                width="100%"
                display="flex"
                justifyContent="center"
                mt={2}
              >
                <IconButton
                  aria-label={isExpanded ? "Show less" : "Show more"}
                  icon={isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                  size="sm"
                  variant="ghost"
                  className="expand-button"
                  color="gray.500"
                  _hover={{ color: "gray.700", bg: "gray.100" }}
                />
              </Box>
            </Box>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default PodcastRecommendationCard; 